// src/app/hooks/useLogin.ts
import { loginMessage } from "app/config";
import type { AuthTokens } from "app/types/auth-tokens";
import { useCallback, useState } from "react";
import tokenService from "services/token.service";
import { useAuthenticateMutation } from "store/api/new.api";
import { useChainId, useSignMessage, useSwitchChain } from "wagmi";
import { useWallet } from "@solana/wallet-adapter-react";
import { useAuthLogger, useErrorLogger, useNetworkLogger } from "app/hooks/useLogger";
import { WalletType } from "app/types/logging.types";
import { useTokenValidation } from "./useTokenValidation";

const ETHEREUM_MAINNET_ID = 1;

export const useLogin = () => {
  const [authenticate, { isLoading: isAuthLoading, error: authError }] =
    useAuthenticateMutation();

  const chainId = useChainId();
  const { switchChain } = useSwitchChain();
  const {
    signMessageAsync,
    status: signStatus,
    error: signError,
  } = useSignMessage();

  const { connected: solanaConnected } = useWallet();
  const [solanaWarning, setSolanaWarning] = useState<string | null>(null);
  const { validateToken } = useTokenValidation();

  // Logging hooks
  const {
    logLoginAttempt,
    logLoginSuccess,
    logLoginError,
    logMessageSign,
    logNetworkSwitch
  } = useAuthLogger();
  const { logJavaScriptError } = useErrorLogger();

  const isMessageSigning = signStatus === "pending";
  const isLoading = isAuthLoading || isMessageSigning;
  const error = authError || signError;

  const loginMutation = useCallback(
    async (address: string, checkSolana: boolean = false): Promise<boolean> => {
      try {
        // Log login attempt
        logLoginAttempt('metamask' as WalletType, {
          address,
          checkSolana,
          solanaConnected,
          currentChainId: chainId,
          requiredChainId: ETHEREUM_MAINNET_ID
        });

        // Check if Solana wallet is required and connected
        if (checkSolana && !solanaConnected) {
          const error = new Error("Solana wallet required but not connected");
          logLoginError('metamask' as WalletType, error, {
            address,
            reason: 'solana_wallet_required',
            checkSolana,
            solanaConnected
          });
          
          setSolanaWarning(
            "Please connect your Solana wallet before logging in"
          );
          return false;
        }

        setSolanaWarning(null);

        // Handle chain switching if needed
        if (chainId !== ETHEREUM_MAINNET_ID) {
          if (!switchChain) {
            const error = new Error("Please switch your wallet to Ethereum Mainnet");
            logLoginError('metamask' as WalletType, error, {
              address,
              reason: 'chain_switch_unavailable',
              currentChainId: chainId,
              requiredChainId: ETHEREUM_MAINNET_ID
            });
            throw error;
          }
          
          logNetworkSwitch(chainId, ETHEREUM_MAINNET_ID, true, {
            reason: 'login_required',
            address
          });
          
          await switchChain({ chainId: ETHEREUM_MAINNET_ID });
        }

        // Log message signing attempt
        logMessageSign('metamask' as WalletType, true, {
          address,
          message: loginMessage,
          chainId: ETHEREUM_MAINNET_ID
        });

        const signature = await signMessageAsync({ message: loginMessage });

        // Log successful message signing (already logged above with success=true)
        // logMessageSigningSuccess('metamask' as WalletType, {
        //   address,
        //   message: loginMessage,
        //   signatureLength: signature.length
        // });

        localStorage.setItem("signature", signature);

        const response = await authenticate({ address }).unwrap();

        const authTokens: AuthTokens = {
          accessToken: response.accessToken,
          user: response.user,
        };
        tokenService.setTokens(authTokens);

        // Validate the new token immediately after login
        console.log('[useLogin] Validating newly received token');
        const validationResult = await validateToken(response.accessToken);

        if (!validationResult.valid) {
          console.error('[useLogin] Token validation failed immediately after login:', validationResult.error);
          tokenService.removeToken();
          logLoginError('metamask' as WalletType, new Error('Token validation failed'), {
            address,
            reason: 'invalid_token_after_login',
            validationError: validationResult.error
          });
          return false;
        }

        console.log('[useLogin] Token validated successfully');

        // Log successful login
        logLoginSuccess('metamask' as WalletType, address, {
          userId: response.user?.address || 'unknown',
          hasAccessToken: !!response.accessToken,
          checkSolana,
          chainId: ETHEREUM_MAINNET_ID,
          tokenValidated: true
        });

        return true;
      } catch (err) {
        console.error("auth error:", err);
        console.error("auth error details:", {
          name: err instanceof Error ? err.name : 'unknown',
          message: err instanceof Error ? err.message : String(err),
          stack: err instanceof Error ? err.stack : undefined,
          signError: signError ? {
            name: signError.name,
            message: signError.message,
          } : null,
        });

        // Log different types of errors
        if (signError) {
          logMessageSign('metamask' as WalletType, false, {
            address,
            message: loginMessage,
            reason: 'message_signing_failed',
            error: signError.message
          });
        } else {
          logLoginError('metamask' as WalletType, err as Error, {
            address,
            reason: 'authentication_failed',
            errorType: err instanceof Error ? err.constructor.name : 'unknown'
          });
        }

        // Log as JavaScript error for general error tracking
        logJavaScriptError(err as Error, 'useLogin_loginMutation');

        return false;
      }
    },
    [authenticate, chainId, signMessageAsync, switchChain, solanaConnected, signError, logLoginAttempt, logLoginSuccess, logLoginError, logMessageSign, logNetworkSwitch, logJavaScriptError]
  );

  return {
    loginMutation,
    isMessageSigning,
    isLoading,
    error,
    solanaWarning,
    setSolanaWarning,
  };
};
